// src/pages/DashboardsPage.tsx
/**
 * Dashboards Page
 * Main dashboard view with TabControl, Filters, and DashboardGroups
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { authStorage, dashboardStorage } from '@/lib/storage';
import TabControl from '@/components/TabControl/TabControl';
import { FilterBar } from '@/components/Filter/FilterBar';
import { SearchBar } from '@/components/Filter/SearchBar';
import { DashboardGroups } from '@/components/DashboardGroups/DashboardGroups';
import DuplicatedItemsModal from '@/components/DashboardGroups/modals/DuplicatedItemsModal';
import { removeMovedCardsFromSource } from '@/components/DashboardGroups/dashboardCache';
import {
  DashboardViewSelector,
  type DashboardViewType,
} from '@/components/Dashboard/DashboardViewSelector';
import { RefreshNotificationButton } from '@/components/Dashboard/RefreshNotificationButton';
import { useSocketRefreshButton, getMoveToTopConversationIds } from '@/hooks/useSocketRefreshButton';
import { useInfoWindow } from '@/contexts/InfoWindowContext';
import { filterService } from '@/services/filterService';
import { useDashboardFilterPersistence } from '@/hooks/useDashboardFilterPersistence';
import { dashboardApi, MIN_COLUMN_WIDTH_PX, gridSizeToColumnWidth } from '@/services/dashboardApi';
import { useDashboardViewSync } from '@/hooks/useDashboardViewSync';
import { dashboardService } from '@/services/dashboardService';
import { setCurrentDashboardId, getSelectedCardTitles } from '@/lib/cardDragState';
import { GridView } from '@/components/GridView/GridView';
import type { GridViewRef } from '@/components/GridView/types';
import Gantt from '@/components/Gantt/Gantt';
import type { GanttConfig } from '@/components/Gantt/Gantt';
import { PhaseDetails } from '@/components/PhaseDetails/PhaseDetails';
import type { PhaseDetailsRef } from '@/components/PhaseDetails/types';
import { PhaseDetailsV2 } from '@/components/PhaseDetailsV2/PhaseDetailsV2';
import type { PhaseDetailsV2Ref } from '@/components/PhaseDetailsV2/types';
import {
  useImportFromExcel,
  IMPORT_EXCEL_DONE_EVENT,
  type ImportExcelDoneDetail,
} from '@/contexts/ImportFromExcelContext';
import { CalendarView } from '@/components/CalendarView';
import { GalleryView } from '@/components/Dashboard/GalleryView';
import { FilesView } from '@/components/Dashboard/FilesView';
import type { TabControlConfig, ReactFunctions, Dashboard } from '@/components/TabControl/types';
import { ReportViewer } from '@/components/Reports/ReportViewer';
import type { FilterOption } from '@/types/dashboard';
import type { DashboardGroupData } from '@/components/DashboardGroups/types';
import { dashboardGroupsService } from '@/components/DashboardGroups/service';
import { dashboardGroupService } from '@/services/dashboardGroupService';
import type { ConversationDetail } from '@/types/conversation';
import { searchHistory } from '@/lib/searchHistory';
import { isDashboardPersonal, setDashboardPersonal } from '@/lib/personalFilterConfig';

// Minimum time between reloads when the user re-clicks the already-active dashboard tab
const TAB_RELOAD_THROTTLE_MS = 2000;

/**
 * Reads the layers hidden via LayerTags and formats them for the API.
 * Mirrors NewsFeedSection: a comma-joined string, empty when nothing is hidden.
 * LayerTags writes this key on every toggle, then dispatches 'layersToHideChanged'.
 */
const readLayersToHide = (): string =>
{
  try
  {
    const arr = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
    return Array.isArray(arr) && arr.length ? arr.join(',') : '';
  }
  catch
  {
    return '';
  }
};

export const DashboardsPage: React.FC = () =>
{
  const { user: _user, IsAdmin } = useAuth();
  void _user;
  const socketConnectionId = useSocketConnectionId();
  const { openImport } = useImportFromExcel();
  const location = useLocation();
  const navigate = useNavigate();

  // Shared dashboard — populated when arriving from a "Copy link" redirect
  const sharedDashboardId: number | null = (location.state as any)?.sharedDashboardId ?? null;
  const [isSharedPublic, setIsSharedPublic] = useState<boolean | null>(null);

  useEffect(() =>
  {
    if (!sharedDashboardId) return;
    dashboardApi
      .getDashboardById(sharedDashboardId)
      .then(d => setIsSharedPublic(d?.IsPublic ?? false))
      .catch(() => setIsSharedPublic(false));
  }, [sharedDashboardId]);

  // Dashboard state - TabControl handles loading and selection
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(null);
  const [pinnedDashboards, setPinnedDashboards] = useState<Dashboard[]>([]);
  // Report viewer state
  const [reportViewerDashboard, setReportViewerDashboard] = useState<Dashboard | null>(null);
  // Bump to force GridView reload after a completed import on this dashboard
  const [gridVersion, setGridVersion] = useState(0);

  // Increments each time the user clicks a tab (even the already-active one) to force
  // the active view to reload and to clear the search text (see useDashboardFilterPersistence)
  const [reloadTrigger, setReloadTrigger] = useState(0);
  // Last tab click, used to throttle repeated reloads of the same dashboard
  const lastTabReloadRef = useRef<{ dashboardId: number; time: number } | null>(null);

  // Per-dashboard filter persistence (keyed by dashboardId); search text resets on reloadTrigger
  const {
    filtersSelected,
    handleFilterSelect,
    handleFilterUnselect,
    handleApplyFilters,
    searchText,
    setSearchText,
    filtersJson,
    extraFilterParams: baseExtraFilterParams,
  } = useDashboardFilterPersistence(currentDashboard?.DashboardId ?? null, reloadTrigger);

  const [isApplyForAll, setIsApplyForAll] = useState(() =>
    !isDashboardPersonal(currentDashboard?.DashboardId ?? 0),
  );

  // Derived state: update isApplyForAll synchronously during the render when the active
  // dashboard changes. A useEffect would run after commit — by then DashboardGroups has
  // already fired an API call with the previous dashboard's stale IsFilterPersonal value.
  const [prevDashboardIdForApplyForAll, setPrevDashboardIdForApplyForAll] = useState(
    currentDashboard?.DashboardId ?? null,
  );
  if ((currentDashboard?.DashboardId ?? null) !== prevDashboardIdForApplyForAll)
  {
    setPrevDashboardIdForApplyForAll(currentDashboard?.DashboardId ?? null);
    setIsApplyForAll(!isDashboardPersonal(currentDashboard?.DashboardId ?? 0));
  }

  const handleApplyFiltersWrapper = useCallback((applyForAll: boolean) =>
  {
    setIsApplyForAll(applyForAll);
    if (currentDashboard?.DashboardId)
    {
      setDashboardPersonal(currentDashboard.DashboardId, !applyForAll);
    }
    handleApplyFilters();
  }, [handleApplyFilters, currentDashboard?.DashboardId]);

  const extraFilterParams = useMemo(
    () => ({ ...baseExtraFilterParams, IsFilterPersonal: !isApplyForAll }),
    [baseExtraFilterParams, isApplyForAll],
  );

  // Filter options / loading state (shared UI concerns, not persisted)
  const [filter, setFilter] = useState<string>('');
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  // View mode state
  const [columnWidth, setColumnWidth] = useState(350);
  const [dashboardViewTypeId, setDashboardViewTypeId] = useState<DashboardViewType>(1);
  const { persistDashboardView, persistDashboardViewDebounced } = useDashboardViewSync();
  const [showAllGalleryImages, setShowAllGalleryImages] = useState(false);
  const [pageSize] = useState(130);

  const [viewSyncDashboardId, setViewSyncDashboardId] = useState<number | null>(null);

  // Derive cardViewMode from dashboardViewTypeId
  const cardViewMode: 'compact' | 'detailed' = dashboardViewTypeId === 2 ? 'detailed' : 'compact';

  // Gantt View state
  const [enableWeekend, setEnableWeekend] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState(0);
  const [ganttLayers, setGanttLayers] = useState<{ LayerId: number; Name: string }[]>([]);

  // Config value ID (default to 1)
  const [configValueId] = useState(1);

  // Resolve view type and grid size on dashboard switch
  // PRIORITY: localStorage stores user's last visited view per dashboard
  if (currentDashboard && currentDashboard.DashboardId !== viewSyncDashboardId)
  {
    setViewSyncDashboardId(currentDashboard.DashboardId);

    let newViewId: DashboardViewType = 1;
    let newGridSize = 350;

    const savedEntry = dashboardStorage.findByContext(
      0,
      configValueId,
      currentDashboard.DashboardId,
    );

    if (savedEntry?.GridSize)
    {
      newGridSize = Math.max(MIN_COLUMN_WIDTH_PX, savedEntry.GridSize);
    }
    else if (currentDashboard.GridSize)
    {
      newGridSize = gridSizeToColumnWidth(currentDashboard.GridSize);
    }

    if (savedEntry?.DashboardViewId)
    {
      // Use saved view from localStorage (user's last visit)
      newViewId = savedEntry.DashboardViewId as DashboardViewType;
    }
    else
    {
      // First-time visit: default to compact view (small cards)
      newViewId = 1;
    }

    setColumnWidth(newGridSize);
    setDashboardViewTypeId(newViewId);
  }

  // Refs for TabControl
  const reactFunctionsRef = useRef<ReactFunctions | null>(null);
  const gridExportRef = useRef<GridViewRef>(null);
  const phaseDetailsExportRef = useRef<PhaseDetailsRef>(null);
  const phaseDetailsV2ExportRef = useRef<PhaseDetailsV2Ref>(null);

  // Ref to background-refresh DashboardGroups from socket notifications
  const dashboardGroupsRefreshRef = useRef<((conversationIds?: number[]) => void) | null>(null);

  // Layers hidden via LayerTags. Only Gantt needs this plumbed through — DashboardGroups
  // reads C2_LayersToHide itself and listens for the same event.
  const [layersToHide, setLayersToHide] = useState<string>(readLayersToHide);

  useEffect(() =>
  {
    const handleLayersChanged = () =>
    {
      setLayersToHide(readLayersToHide());
    };

    window.addEventListener('layersToHideChanged', handleLayersChanged);
    return () => window.removeEventListener('layersToHideChanged', handleLayersChanged);
  }, []);

  // Check if current dashboard has dynamic groups (IsDefault=true)
  const hasDynamicGroups = useMemo(
    () => currentDashboard?.DashboardGroups?.some(g => g.IsDefault === true) ?? false,
    [currentDashboard],
  );

  // Socket refresh notifications
  const {
    hasPendingUpdates,
    pendingCount,
    pendingOrderDashboard,
    pendingDashboardList,
    triggerRefresh,
    clearConversationHighlight,
    pendingGroupIds,
    pendingConversationIds,
  } = useSocketRefreshButton({
    currentDashboardId: currentDashboard?.DashboardId ?? null,
    configValueId,
    hasDynamicGroups,
    onRefresh: updates => dashboardGroupsRefreshRef.current?.(getMoveToTopConversationIds(updates)),
    onRefreshDashboardList: () => reactFunctionsRef.current?.reloadDashboard(false),
    onRefreshDashboardOrder: () =>
      reactFunctionsRef.current?.reloadDashboard(false, undefined, true),
    onDashboardRemoved: () => reactFunctionsRef.current?.reloadDashboard(false),
  });

  // Get openInfoWindow from context
  const { openInfoWindow } = useInfoWindow();

  // Get API configuration
  const getApiConfig = useCallback(() =>
  {
    const token = authStorage.getAccessToken() || '';
    const apiUrl =
      import.meta.env.VITE_API_URL ||
      import.meta.env.VITE_JS_apiURL ||
      'http://localhost/Corgee.Web/';

    return {
      url: apiUrl,
      token: token,
      refreshToken: '',
      TenancyName: import.meta.env.VITE_TENANCY_NAME || 'cgbot',
    };
  }, []);

  const isDark = document.documentElement.classList.contains('dark');

  // True once the user has typed in the filter input
  const filterTouchedRef = useRef<boolean>(false);

  // Load filter options when filter text changes
  useEffect(() =>
  {
    const loadFilterOptions = async () =>
    {
      if (filter.length > 0) filterTouchedRef.current = true;
      // Below the search threshold, reload the full list instead of clearing it
      const searchTerm = filter.length >= 2 ? filter : null;
      // On first mount the full list is loaded by onFilterOpened instead
      if (!searchTerm && !filterTouchedRef.current) return;

      try
      {
        setFilterLoading(true);
        setFilterError(null);
        const options = await filterService.getFilterOptions(searchTerm, {
          IsFromProject: false,
          ConfigValueId: configValueId,
        });
        setFilterOptions(options);
      }
      catch (err)
      {
        console.error('Error loading filter options:', err);
        setFilterError('Error loading filters');
      }
      finally
      {
        setFilterLoading(false);
      }
    };

    const debounceTimeout = setTimeout(loadFilterOptions, 300);
    return () => clearTimeout(debounceTimeout);
  }, [filter, configValueId]);

  // Load initial filter options when FilterIcon is clicked
  const handleFilterOpened = useCallback(async () =>
  {
    try
    {
      setFilterLoading(true);
      setFilterError(null);
      const options = await filterService.getFilterOptions(null, {
        IsFromProject: false,
        ConfigValueId: configValueId,
      });
      setFilterOptions(options);
    }
    catch (err)
    {
      console.error('Error loading filter options:', err);
      setFilterError('Error loading filters');
      setFilterLoading(false);
    }
    finally
    {
      setFilterLoading(false);
    }
  }, [configValueId]);

  // Refs to hold latest view/grid without stale closures
  const dashboardViewTypeIdRef = useRef<DashboardViewType>(dashboardViewTypeId);
  useEffect(() =>
  {
    dashboardViewTypeIdRef.current = dashboardViewTypeId;
  }, [dashboardViewTypeId]);
  const columnWidthRef = useRef<number>(columnWidth);
  useEffect(() =>
  {
    columnWidthRef.current = columnWidth;
  }, [columnWidth]);
  const pinnedDashboardsRef = useRef<Dashboard[]>([]);
  useEffect(() =>
  {
    pinnedDashboardsRef.current = pinnedDashboards;
  }, [pinnedDashboards]);

  const saveDashboardState = useCallback(
    (dashboard: Dashboard, viewId: DashboardViewType, gridSize: number) =>
    {
      const pinned = pinnedDashboardsRef.current;
      const index = pinned.findIndex(d => d.DashboardId === dashboard.DashboardId);
      dashboardStorage.updateDashboard({
        projectId: 0,
        configValueId: configValueId,
        index: index >= 0 ? index : 0,
        dashboardId: dashboard.DashboardId,
        DashboardViewId: viewId,
        GridSize: gridSize,
        lastModified: Date.now(),
      });
    },
    [configValueId],
  );

  // Sync GridSize and DashboardViewId when dashboard changes
  useEffect(() =>
  {
    if (!currentDashboard) return;
    saveDashboardState(currentDashboard, dashboardViewTypeIdRef.current, columnWidthRef.current);
  }, [currentDashboard, saveDashboardState]);

  // Update browser tab title when dashboard changes
  useEffect(() =>
  {
    document.title = currentDashboard?.Name || 'Dashboards';
    setCurrentDashboardId(currentDashboard?.DashboardId ?? 0);
  }, [currentDashboard]);

  // Save dashboard visit to search history
  useEffect(() =>
  {
    if (currentDashboard?.DashboardId && currentDashboard?.Name)
    {
      searchHistory.addDashboardVisit(
        currentDashboard.DashboardId,
        currentDashboard.Name,
        null,
        'DASHBOARDS',
      );
    }
  }, [currentDashboard]);

  // Reload GridView when an import finishes on this exact dashboard and view 4 is active
  useEffect(() =>
  {
    const handler = (e: Event) =>
    {
      const { dashboardId } = (e as CustomEvent<ImportExcelDoneDetail>).detail;
      if (dashboardId === currentDashboard?.DashboardId && dashboardViewTypeIdRef.current === 4)
      {
        setGridVersion(v => v + 1);
      }
    };
    window.addEventListener(IMPORT_EXCEL_DONE_EVENT, handler);
    return () => window.removeEventListener(IMPORT_EXCEL_DONE_EVENT, handler);
  }, [currentDashboard]);

  // TabControl handlers
  const handleSetDashboards = useCallback(
    (
      _allDashboards: Dashboard[],
      pinned: Dashboard[],
      _notPinned: Dashboard[],
      current: Dashboard | undefined,
      _isInitial?: boolean,
    ) =>
    {
      setPinnedDashboards(pinned);
      if (current)
      {
        setCurrentDashboard(current);
      }
    },
    [],
  );

  const handleSetDashboard = useCallback((dashboard: Dashboard) =>
  {
    setCurrentDashboard(dashboard);

    // Switching dashboards always reloads; re-clicking the same tab is throttled
    const now = Date.now();
    const last = lastTabReloadRef.current;
    if (last && last.dashboardId === dashboard.DashboardId && now - last.time < TAB_RELOAD_THROTTLE_MS) return;

    lastTabReloadRef.current = { dashboardId: dashboard.DashboardId, time: now };
    setReloadTrigger(prev => prev + 1);
  }, []);

  const handleAddDashboard = useCallback((_event: React.MouseEvent) => {}, []);

  const handleUpdateDashboard = useCallback(
    async (_event: React.MouseEvent, dashboard: Dashboard) =>
    {
      try
      {
        const updated = await dashboardApi.updateDashboard(dashboard);
        setCurrentDashboard(prev =>
          prev?.DashboardId === updated.DashboardId ? { ...prev, ...updated } : prev,
        );
        setPinnedDashboards(prev => prev.map(d =>
          d.DashboardId === updated.DashboardId ? { ...d, ...updated } : d,
        ));
      }
      catch (error)
      {
        console.error('Error updating dashboard:', error);
      }
    },
    [],
  );

  const handlePinDashboard = useCallback(
    async (_event: React.MouseEvent, dashboard: Dashboard) =>
    {
      // Act on any dashboard with a pin-actionable flag, not just the shared one
      if (!dashboard.IsPineableDashboard && !dashboard.IsPinedDashboard) return;
      try
      {
        if (dashboard.IsPineableDashboard)
        {
          const order = (pinnedDashboards?.length || 0) + 1;
          await dashboardService.AddPinedDashboard(dashboard.DashboardId, order);
          await dashboardService.AddPinedTabDashboard(
            dashboard.DashboardId,
            order,
            configValueId,
            0,
            true,
            socketConnectionId,
          );
        }
        else if (dashboard.IsPinedDashboard)
        {
          await dashboardService.DeletePinedDashboard(dashboard.DashboardId);
          await dashboardService.DeletePinedTabDashboard(
            configValueId,
            dashboard.DashboardId,
            0,
            socketConnectionId,
          );

          // Forget the shared-dashboard tracking so it doesn't reappear after refresh
          if (sharedDashboardId === dashboard.DashboardId)
          {
            navigate(location.pathname, { replace: true, state: {} });
          }
        }
      }
      catch (err)
      {
        console.error('Error updating pin state:', err);
      }
    },
    [pinnedDashboards, configValueId, socketConnectionId, sharedDashboardId, navigate, location.pathname],
  );

  const handleDeleteDashboard = useCallback(
    (_event: React.MouseEvent, _dashboard: Dashboard) => {},
    [],
  );

  const handleOpenTabDashboard = useCallback((_event: React.MouseEvent, dashboard: Dashboard) =>
  {
    setCurrentDashboard(dashboard);
  }, []);

  const attachReactFunctions = useCallback((functions: ReactFunctions) =>
  {
    reactFunctionsRef.current = functions;
  }, []);

  const showReports = useCallback((_event: React.MouseEvent, dashboard: Dashboard) =>
  {
    setReportViewerDashboard(dashboard);
  }, []);

  const onDuplicated = useCallback((newDashboardId?: number) =>
  {
    if (newDashboardId !== undefined)
    {
      reactFunctionsRef.current?.reloadDashboard(false, newDashboardId);
    }
    else
    {
      reactFunctionsRef.current?.reloadDashboard(true);
    }
  }, []);

  // Drag-to-tab notification
  const [dragTabNotification, setDragTabNotification] = useState<string | null>(null);
  const dragTabNotificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ShowDragTabNotification = useCallback((message: string) =>
  {
    if (dragTabNotificationTimerRef.current)
    {
      clearTimeout(dragTabNotificationTimerRef.current);
    }
    setDragTabNotification(message);
    dragTabNotificationTimerRef.current = setTimeout(() => setDragTabNotification(null), 3000);
  }, []);

  // Duplicated items modal state
  interface PendingDragOp {
    targetDashboard: Dashboard;
    conversationIds: number[];
    sourceGroupIds: Map<number, number>; // convId → groupId
    sourceDashboardId: number;
    isCopy: boolean;
  }
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const pendingDragOpRef = useRef<PendingDragOp | null>(null);

  // Group drag-to-tab duplicate confirmation
  interface PendingGroupDrop {
    targetDashboard: Dashboard;
    sourceGroupId: number;
    sourceDashboardId: number;
    isCopy: boolean;
  }
  const [groupDuplicateModalOpen, setGroupDuplicateModalOpen] = useState(false);
  const pendingGroupDropRef = useRef<PendingGroupDrop | null>(null);

  const ExecuteDragOp = useCallback(
    async (op: PendingDragOp, removeDuplicates: boolean) =>
    {
      try
      {
        await dashboardService.addDashboardGroupByParametersDrag({
          dashboardIdSendTo: op.targetDashboard.DashboardId,
          conversationIds: op.conversationIds.join(','),
          removeDuplicates,
          moveAll: removeDuplicates,
          dashboardIdCurrent: op.sourceDashboardId,
          isFromMoveTo: null,
          ParentConversationId: 0,
          dashboardGroupOrigin: 0,
          socketConnectionId: socketConnectionId || '',
        });

        if (!op.isCopy)
        {
          for (const convId of op.conversationIds)
          {
            const groupId = op.sourceGroupIds.get(convId) ?? 0;
            // groupId = 0 means dragged from InfoWindow (no column context) → skip delete
            if (groupId !== 0)
            {
              await dashboardService.deleteDashboardGroupConversation({
                DashboardGroupId: groupId,
                ConversationId: convId,
                IsFromBookmark: false,
                layersToHide: 0,
                socketConnectionId: socketConnectionId || '',
              });
            }
          }
        }

        const uniqueGroupIds = new Set(op.sourceGroupIds.values());
        uniqueGroupIds.forEach(gid =>
        {
          if (gid !== 0)
          {
            dashboardService.updatePercentDashboardGroupConversation(gid).catch(() => {});
          }
        });

        const count = op.conversationIds.length;
        ShowDragTabNotification(op.isCopy ? `${count} items copied` : `${count} items moved`);

        if (!op.isCopy)
        {
          removeMovedCardsFromSource(
            op.sourceDashboardId,
            op.conversationIds.map(id => ({ conversationId: id, groupId: op.sourceGroupIds.get(id) })),
          );
          dashboardGroupsRefreshRef.current?.();
        }
      }
      catch (err)
      {
        // swallow — user already sees no feedback on silent failures
      }
    },
    [socketConnectionId, ShowDragTabNotification],
  );

  const HandleDuplicateModalOk = useCallback(() =>
  {
    setDuplicateModalOpen(false);
    const op = pendingDragOpRef.current;
    if (op)
    {
      ExecuteDragOp(op, false);
    }
  }, [ExecuteDragOp]);

  const HandleDuplicateMoveAll = useCallback(() =>
  {
    setDuplicateModalOpen(false);
    const op = pendingDragOpRef.current;
    if (op)
    {
      ExecuteDragOp(op, true);
    }
  }, [ExecuteDragOp]);

  const HandleDuplicateCancel = useCallback(() =>
  {
    setDuplicateModalOpen(false);
    pendingDragOpRef.current = null;
  }, []);

  const HandleCardDroppedOnTab = useCallback(
    async (
      targetDashboard: Dashboard,
      conversationId: number,
      sourceGroupId: number,
      sourceDashboardId: number,
      isCopy: boolean,
    ) =>
    {
      if (!currentDashboard)
      {
        return;
      }
      try
      {
        // If this card is part of a multi-selection, move all selected cards
        const selectedTitles = getSelectedCardTitles();
        const isMultiDrag =
          selectedTitles.length > 1 && selectedTitles.some(s => s.id === conversationId);

        let conversationIds: number[];
        let sourceGroupIds: Map<number, number>;

        if (isMultiDrag)
        {
          conversationIds = selectedTitles.map(s => s.id);
          sourceGroupIds = new Map(
            selectedTitles.map(s => [s.id, s.groupId ?? sourceGroupId]),
          );
        }
        else
        {
          conversationIds = [conversationId];
          sourceGroupIds = new Map([[conversationId, sourceGroupId]]);
        }

        const isDuplicated = await dashboardService.getConversationsDuplicated(
          targetDashboard.DashboardId,
          null,
          conversationIds.join(','),
        );

        const op: PendingDragOp = {
          targetDashboard,
          conversationIds,
          sourceGroupIds,
          sourceDashboardId,
          isCopy,
        };

        if (isDuplicated)
        {
          pendingDragOpRef.current = op;
          setDuplicateModalOpen(true);
        }
        else
        {
          ExecuteDragOp(op, false);
        }
      }
      catch (err)
      {
        // swallow — user already sees no feedback on silent failures
      }
    },
    [currentDashboard, ExecuteDragOp],
  );

  const ExecuteGroupDrop = useCallback(
    async (op: PendingGroupDrop) =>
    {
      try
      {
        await dashboardGroupsService.AddDashboardGroupByParametersValidateCompatibleItems(
          op.targetDashboard.DashboardId,
          op.sourceGroupId,
          '0',
          0,
          true,
          configValueId,
          undefined,
          0,
          op.sourceGroupId,
        );
        if (!op.isCopy)
        {
          await dashboardGroupService.deleteDashboardGroup(op.sourceGroupId, '');
          dashboardGroupsRefreshRef.current?.();
        }
        ShowDragTabNotification(op.isCopy ? 'Group copied' : 'Group moved');
      }
      catch (err)
      {
        console.error('Error moving/copying group:', err);
      }
    },
    [configValueId, ShowDragTabNotification],
  );

  const HandleGroupDroppedOnTab = useCallback(
    async (
      targetDashboard: Dashboard,
      sourceGroupId: number,
      sourceDashboardId: number,
      isCopy: boolean,
    ) =>
    {
      if (!currentDashboard) return;
      try
      {
        const op: PendingGroupDrop = { targetDashboard, sourceGroupId, sourceDashboardId, isCopy };
        const hasDuplicates = await dashboardGroupsService.GetConversationsDuplicated(
          targetDashboard.DashboardId,
          sourceGroupId,
          '',
        );
        if (hasDuplicates)
        {
          pendingGroupDropRef.current = op;
          setGroupDuplicateModalOpen(true);
        }
        else
        {
          ExecuteGroupDrop(op);
        }
      }
      catch (err)
      {
        console.error('Error in group drop handler:', err);
      }
    },
    [currentDashboard, ExecuteGroupDrop],
  );

  // TabControl configuration
  const tabControlConfig: TabControlConfig = {
    api: getApiConfig(),
    configValueId: configValueId,
    projectId: undefined,
    DashboardId: currentDashboard?.DashboardId,
    isOrganize: false,
    isBookmarks: false,
    isProjectDetail: false,
    isDashboardShared: !!sharedDashboardId,
    dashboardSharedId: sharedDashboardId ?? undefined,
    recycleBinDashboard: false,
    canEdit: true,
    isAdmin: IsAdmin(),
    webUrl: import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/',
    functions: {
      setDashboards: handleSetDashboards,
      setDashboard: handleSetDashboard,
      addDashboard: handleAddDashboard,
      updateDashboard: handleUpdateDashboard,
      pinDashboard: handlePinDashboard,
      deleteDashboard: handleDeleteDashboard,
      OpenTabDashboard: handleOpenTabDashboard,
      attachReactFunctions: attachReactFunctions,
      showReports,
      onDuplicated,
      getSocketConnectionId: () => socketConnectionId,
      onCardDroppedOnTab: HandleCardDroppedOnTab,
      onGroupDroppedOnTab: HandleGroupDroppedOnTab,
    },
  };

  // True when the active tab is the shared dashboard and it has no public access
  const isCurrentSharedNoPermission =
    !!sharedDashboardId &&
    currentDashboard?.DashboardId === sharedDashboardId &&
    isSharedPublic === false;

  // DashboardGroups callbacks
  const handleDataLoaded = useCallback((_groups: DashboardGroupData[]) => {}, []);

  const handleGroupsError = useCallback((error: Error) =>
  {
    console.error('Dashboard groups error:', error);
  }, []);

  const handleCardMoved = useCallback(
    (_conversationId: number, _fromGroupId: number, _toGroupId: number) => {},
    [],
  );

  const handleOpenInfoWindow = useCallback(
    (conversation: ConversationDetail) =>
    {
      clearConversationHighlight(conversation.ConversationId);
      openInfoWindow(conversation.ConversationId, undefined, conversation);
    },
    [openInfoWindow, clearConversationHighlight],
  );

  // Apply persisted view and grid size to local state
  const applyDashboardView = useCallback(
    (dashboardId: number, viewType: DashboardViewType, apiGridSize: number) =>
    {
      setCurrentDashboard(prev => prev && prev.DashboardId === dashboardId ? {
        ...prev,
        DashboardViewTypeId: viewType,
        GridSize: apiGridSize,
      } : prev);

      setPinnedDashboards(prev => prev.map(d =>
        d.DashboardId === dashboardId
          ? { ...d, DashboardViewTypeId: viewType, GridSize: apiGridSize }
          : d,
      ));

      reactFunctionsRef.current?.updateDashboardView(dashboardId, viewType, apiGridSize);
    },
    [],
  );

  const handleViewChange = useCallback(
    (viewType: DashboardViewType) =>
    {
      setDashboardViewTypeId(viewType);
      if (!currentDashboard) return;
      const gridSize = columnWidthRef.current;
      saveDashboardState(currentDashboard, viewType, gridSize);

      persistDashboardView(currentDashboard.DashboardId, viewType, gridSize, apiGridSize =>
      {
        applyDashboardView(currentDashboard.DashboardId, viewType, apiGridSize);
      });
    },
    [currentDashboard, saveDashboardState, persistDashboardView, applyDashboardView],
  );

  // Persist column resize (debounced)
  const handleColumnWidthChange = useCallback(
    (newWidth: number) =>
    {
      setColumnWidth(newWidth);
      if (!currentDashboard) return;

      const viewType = dashboardViewTypeIdRef.current;
      saveDashboardState(currentDashboard, viewType, newWidth);

      persistDashboardViewDebounced(
        currentDashboard.DashboardId,
        viewType,
        newWidth,
        apiGridSize =>
        {
          applyDashboardView(currentDashboard.DashboardId, viewType, apiGridSize);
        },
      );
    },
    [currentDashboard, saveDashboardState, persistDashboardViewDebounced, applyDashboardView],
  );

  return (
    <>
      <div className="dashboards-page h-full flex flex-col">
        {/* Drag-to-tab notification banner */}
        {dragTabNotification && (
          <div
            style={{
              position: 'fixed',
              top: '70px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              background: '#62aacb',
              color: '#fff',
              borderRadius: '8px',
              boxShadow: '0 3px 6px rgba(0,0,0,.2)',
              padding: '6px 20px',
              fontSize: '14px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {dragTabNotification}
          </div>
        )}

        {/* Duplicated items modal (card drag-to-tab) */}
        <DuplicatedItemsModal
          open={duplicateModalOpen}
          isCopy={pendingDragOpRef.current?.isCopy}
          onCancel={HandleDuplicateCancel}
          onMoveAll={HandleDuplicateMoveAll}
          onOk={HandleDuplicateModalOk}
        />

        {/* Duplicate confirmation modal (group drag-to-tab) */}
        <DuplicatedItemsModal
          open={groupDuplicateModalOpen}
          isCopy={pendingGroupDropRef.current?.isCopy}
          onCancel={() =>
          {
            setGroupDuplicateModalOpen(false);
            pendingGroupDropRef.current = null;
          }}
          onMoveAll={() =>
          {
            setGroupDuplicateModalOpen(false);
            const op = pendingGroupDropRef.current;
            if (op) ExecuteGroupDrop({ ...op, isCopy: false });
          }}
          onOk={() =>
          {
            setGroupDuplicateModalOpen(false);
            const op = pendingGroupDropRef.current;
            if (op) ExecuteGroupDrop(op);
          }}
        />

        {/* TabControl Section */}
        <div className="border border-gray-200 rounded-lg bg-gray-50">
          <TabControl config={tabControlConfig} />
        </div>

        {/* Filters Section */}
        <div
          className="flex items-center justify-between gap-2 mt-1.5"
          style={{ paddingLeft: '15px', paddingRight: '15px' }}
        >
          <div className="flex items-center gap-2">
            <FilterBar
              key={currentDashboard?.DashboardId ?? 0}
              filter={filter}
              onChangeFilter={setFilter}
              loading={filterLoading}
              filterOptions={filterOptions}
              error={filterError}
              filtersSelected={filtersSelected}
              onFilterOpened={handleFilterOpened}
              onFilterSelect={handleFilterSelect}
              onFilterUnselect={handleFilterUnselect}
              isApplyForAll={isApplyForAll}
              onApplyFilters={handleApplyFiltersWrapper}
            />
            <SearchBar filterSearchCard={searchText} onChangeSearchFilterCards={setSearchText} />
          </div>
          <div className="flex items-center gap-1">
            {dashboardViewTypeId !== 3 &&
              dashboardViewTypeId !== 4 &&
              dashboardViewTypeId !== 5 &&
              dashboardViewTypeId !== 6 &&
              dashboardViewTypeId !== 7 &&
              dashboardViewTypeId !== 9 &&
              dashboardViewTypeId !== 8 && (
              <input
                type="range"
                min="250"
                max="800"
                value={columnWidth}
                onChange={e => handleColumnWidthChange(Number(e.target.value))}
                className="w-24 h-1 custom-slider"
                style={{ accentColor: '#62aacb' }}
              />
            )}
            {dashboardViewTypeId === 7 && (
              <>
                <label
                  className="flex items-center gap-1 text-xs cursor-pointer"
                  style={{
                    color: isDark ? '#6e6860' : 'rgba(0,0,0,0.6)',
                    fontSize: '10px',
                    paddingRight: '15px',
                    paddingTop: '7px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={enableWeekend}
                    onChange={e => setEnableWeekend(e.target.checked)}
                    className="w-3 h-3"
                  />
                  Enable Weekend
                </label>
                <select
                  className="text-xs border rounded px-1 py-0.5"
                  style={{
                    color: isDark ? '#a9a093' : 'rgba(0,0,0,0.7)',
                    backgroundColor: isDark ? '#3a3c3c' : undefined,
                    borderColor: isDark ? '#4a4c4c' : undefined,
                  }}
                  value={selectedLayerId}
                  onChange={e => setSelectedLayerId(Number(e.target.value))}
                >
                  {ganttLayers.map(layer => (
                    <option key={layer.LayerId} value={layer.LayerId}>
                      {layer.Name}
                    </option>
                  ))}
                </select>
              </>
            )}
            {(dashboardViewTypeId === 4 || dashboardViewTypeId === 11) && (
              <button
                className="view-bar-btn"
                title="Export to Excel"
                onClick={() => gridExportRef.current?.exportToExcel()}
              >
                <i className="material-icons">cloud_download</i>
                <span>Export</span>
              </button>
            )}
            {dashboardViewTypeId === 9 && (
              <button
                className="view-bar-btn"
                title="Export to Excel"
                onClick={() => phaseDetailsExportRef.current?.exportToExcel()}
              >
                <i className="material-icons">cloud_download</i>
                <span>Export</span>
              </button>
            )}
            {dashboardViewTypeId === 8 && (
              <button
                className="view-bar-btn"
                title="Export to Excel"
                onClick={() => phaseDetailsV2ExportRef.current?.exportToExcel()}
              >
                <i className="material-icons">cloud_download</i>
                <span>Export</span>
              </button>
            )}
            {dashboardViewTypeId === 4 && (
              <button
                className="view-bar-btn"
                title="Import from Excel"
                onClick={() =>
                  currentDashboard &&
                  openImport({
                    dashboardId: currentDashboard.DashboardId,
                    dashboardGroups: currentDashboard.DashboardGroups ?? [],
                    configValueId,
                  })
                }
                disabled={!currentDashboard}
              >
                <i className="material-icons">cloud_upload</i>
                <span>Import</span>
              </button>
            )}
            {dashboardViewTypeId === 6 && (
              <div className="flex items-center gap-1.5">
                <input
                  id="chkShowAllGallery"
                  type="checkbox"
                  checked={showAllGalleryImages}
                  onChange={e => setShowAllGalleryImages(e.target.checked)}
                  className="cursor-pointer"
                />
                <label htmlFor="chkShowAllGallery" className="cursor-pointer select-none text-xs font-normal text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  Show all attached images
                </label>
              </div>
            )}
            <DashboardViewSelector
              currentViewTypeId={dashboardViewTypeId}
              onViewChange={handleViewChange}
            />
            <RefreshNotificationButton
              hasPendingUpdates={hasPendingUpdates}
              pendingCount={pendingCount}
              onClick={triggerRefresh}
              message={
                pendingDashboardList
                  ? 'Your dashboards have changed, click here to refresh'
                  : pendingOrderDashboard
                    ? 'Your dashboards have been reordered, click here to refresh'
                    : undefined
              }
            />
          </div>
        </div>

        {/* No-permission message for shared dashboard */}
        {isCurrentSharedNoPermission && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-400 mt-8">
            <i className="fa fa-lock" style={{ fontSize: '48px' }}></i>
            <p className="text-base">You don't have permission to view this dashboard.</p>
          </div>
        )}

        {/* Main Content Area - conditional on currentDashboard and viewType */}
        {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 3 && (
          <div className="flex-1 min-h-0 overflow-auto mt-2">
            <CalendarView
              key={`cal-${configValueId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
              dashboardId={currentDashboard.DashboardId}
              configValueId={configValueId}
              filters={filtersJson}
              searchText={searchText.trim()}
              layersToHide={layersToHide}
              extraFilterParams={extraFilterParams}
              onOpenInfoWindow={id => openInfoWindow(id)}
              onRegisterRefresh={fn =>
              {
                dashboardGroupsRefreshRef.current = fn;
              }}
            />
          </div>
        )}

        {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 6 && (
          <div className="flex-1 min-h-0 overflow-auto mt-2">
            <GalleryView
              key={`gallery-${configValueId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
              dashboardId={currentDashboard.DashboardId}
              projectId=""
              filters={filtersJson}
              searchText={searchText.trim()}
              extraFilterParams={extraFilterParams}
              onOpenInfoWindow={id => openInfoWindow(id)}
              showAllImages={showAllGalleryImages}
            />
          </div>
        )}

        {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 5 && (
          <div className="flex-1 min-h-0 overflow-auto mt-2">
            <FilesView
              key={`files-${configValueId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
              dashboardId={currentDashboard.DashboardId}
              projectId=""
              filters={filtersJson}
              searchText={searchText.trim()}
              extraFilterParams={extraFilterParams}
              onOpenInfoWindow={id => openInfoWindow(id)}
            />
          </div>
        )}

        {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 7 && (
          <div className="gantt-wrapper gantt-container">
            <Gantt
              key={`gantt-${configValueId}-${currentDashboard.DashboardId}-layer${selectedLayerId}-hide${layersToHide}-weekend${enableWeekend}-${filtersJson}-${searchText}-r${reloadTrigger}`}
              dashboard={{
                DashboardId: currentDashboard.DashboardId,
                DashboardViewTypeId: 7,
                LayersToHide: layersToHide,
                PageSize: 40,
                PageNumber: 1,
                Filters: filtersJson,
                SearchText: searchText.trim(),
                ...extraFilterParams,
              }}
              config={
                {
                  apiUrl: getApiConfig().url,
                  authToken: getApiConfig().token,
                  selectedLayerId: selectedLayerId,
                  WorkWeekend: enableWeekend,
                  openInfoWindow: (conversationId: number) => openInfoWindow(conversationId),
                  loadGanttLayers: (layers: any[]) => setGanttLayers(layers),
                } as GanttConfig
              }
            />
          </div>
        )}

        {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 9 && (
          <div className="flex-1 min-h-0 overflow-hidden mt-2">
            <PhaseDetails
              ref={phaseDetailsExportRef}
              key={`phase-${currentDashboard.DashboardId}-r${reloadTrigger}`}
              dashboardId={currentDashboard.DashboardId}
              dashboardName={currentDashboard.Name}
              filters={filtersJson}
              searchText={searchText.trim()}
              layersToHide={layersToHide}
              extraFilterParams={extraFilterParams}
              isActive={true}
              onOpenInfoWindow={handleOpenInfoWindow}
            />
          </div>
        )}

        {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 8 && (
          <div className="flex-1 min-h-0 overflow-hidden mt-2">
            <PhaseDetailsV2
              ref={phaseDetailsV2ExportRef}
              key={`phase-v2-${currentDashboard.DashboardId}-r${reloadTrigger}`}
              dashboardId={currentDashboard.DashboardId}
              dashboardName={currentDashboard.Name}
              filters={filtersJson}
              searchText={searchText.trim()}
              layersToHide={layersToHide}
              extraFilterParams={extraFilterParams}
              isActive={true}
              onOpenInfoWindow={handleOpenInfoWindow}
            />
          </div>
        )}

        {!isCurrentSharedNoPermission &&
          currentDashboard &&
          dashboardViewTypeId !== 3 &&
          dashboardViewTypeId !== 5 &&
          dashboardViewTypeId !== 6 &&
          dashboardViewTypeId !== 7 &&
          dashboardViewTypeId !== 9 &&
          dashboardViewTypeId !== 8 && (
          <div className="flex-1 min-h-0 overflow-hidden mt-2">
            {dashboardViewTypeId === 4 ? (
              <GridView
                ref={gridExportRef}
                key={`${currentDashboard.DashboardId}-v${gridVersion}-r${reloadTrigger}`}
                dashboardId={currentDashboard.DashboardId}
                dashboardName={currentDashboard.Name}
                filters={filtersJson}
                searchText={searchText.trim()}
                layersToHide={layersToHide}
                extraFilterParams={extraFilterParams}
                isActive={true}
                onOpenInfoWindow={handleOpenInfoWindow}
              />
            ) : (
              <DashboardGroups
                key={currentDashboard.DashboardId}
                dashboardId={currentDashboard.DashboardId}
                projectId=""
                filters={filtersJson}
                searchText={searchText.trim()}
                pageSize={pageSize}
                cardViewMode={cardViewMode}
                columnWidth={columnWidth}
                dashboardViewTypeId={dashboardViewTypeId}
                configValueId={configValueId}
                extraFilterParams={extraFilterParams}
                onDataLoaded={handleDataLoaded}
                onError={handleGroupsError}
                onCardMoved={handleCardMoved}
                onOpenInfoWindow={handleOpenInfoWindow}
                onRegisterRefresh={fn =>
                {
                  dashboardGroupsRefreshRef.current = fn;
                }}
                pendingGroupIds={pendingGroupIds}
                pendingConversationIds={pendingConversationIds}
                reloadTrigger={reloadTrigger}
              />
            )}
          </div>
        )}
      </div>

      {reportViewerDashboard && (
        <ReportViewer
          dashboard={reportViewerDashboard}
          projectId={0}
          onClose={() => setReportViewerDashboard(null)}
          onReportCreated={() => {}}
          onReportDeleted={() => setReportViewerDashboard(null)}
        />
      )}
    </>
  );
};

export default DashboardsPage;
